import { ComparisonMode, ReportDateRange, WidgetSpec } from "../../../models/ReportBuilder";

export interface ReportTemplateWidget {
	spec: WidgetSpec;
	/** The prompt shown when refining the widget. */
	prompt: string;
}

export interface ReportTemplate {
	name: string;
	/** One line describing what the template contains, shown when picking a template. */
	description?: string;
	range: ReportDateRange;
	compare?: ComparisonMode;
	/** Rows of widgets, top to bottom; at most MAX_PER_ROW per row. */
	rows: ReportTemplateWidget[][];
}

/** Every widget of a template in reading order. */
export const templateWidgets = (template: ReportTemplate): ReportTemplateWidget[] =>
	template.rows.flat();

/** The time bucket the backend binds for the chosen grain (auto = by range length, as the legacy KPI page did). */
const PERIOD = ":bucket(start_date)";

/** Rows that count on the KPI page: no cancelled jobs, no absence; the report's filters apply. */
const FACTS =
	"from work_facts where start_date between :from and :to and job_status <> 'CANCELLED' and absence_type is null and :filter(customer_id) and :filter(pool_id) and :filter(consultant_id) and :filter(contract_id)";

const card = (
	title: string,
	description: string,
	sql: string,
	key: string,
	unit?: string
): WidgetSpec => ({
	title,
	description,
	sql,
	chart: { type: "number", yKeys: [key], unit },
	usesDateRange: true,
	usesFilters: true,
});

/**
 * The legacy KPI dashboard rebuilt on the lake: two sections, each a row of
 * four cards over a full-width combo panel, one week at a time versus the
 * same week last year.
 */
export const KPI_TEMPLATE: ReportTemplate = {
	name: "KPI-översikt",
	description:
		"Bruttovinst, omsättning, sålda timmar och bokningar – en vecka i taget mot samma vecka förra året.",
	range: { preset: "this_week" },
	compare: "same_period_last_year",
	rows: [
		// Ekonomi: four cards, then the combo panel.
		[
			{
				prompt: "Omsättning totalt",
				spec: card(
					"Omsättning",
					"Summa intäkt i perioden, inkl. bokade ej rapporterade pass.",
					`select round(sum(revenue), 0) as omsättning ${FACTS}`,
					"omsättning",
					"kr"
				),
			},
			{
				prompt: "Bruttovinst totalt",
				spec: card(
					"Bruttovinst",
					"Intäkt minus lönekostnad (× 1,55) i perioden.",
					`select round(sum(gross_profit), 0) as bruttovinst ${FACTS}`,
					"bruttovinst",
					"kr"
				),
			},
			{
				prompt: "Sålda timmar totalt",
				spec: card(
					"Sålda timmar",
					"Fakturerade timmar, annars rapporterade (planerade för bokade ej rapporterade pass).",
					`select round(sum(billable_hours), 0) as sålda_timmar ${FACTS}`,
					"sålda_timmar",
					"h"
				),
			},
			{
				prompt: "Bokade pass totalt",
				spec: card(
					"Bokade pass",
					"Pass med tidrapport i perioden.",
					`select count(*) filter (where tr_status is not null) as bokade_pass ${FACTS}`,
					"bokade_pass"
				),
			},
		],
		[
			{
				prompt: "Ekonomi: bruttovinst som staplar och omsättning som linje per period",
				spec: {
					title: "Ekonomi",
					description:
						"Bruttovinst (staplar) och omsättning (linje) per period, exkl. moms. Bokade pass räknas med planerad tid tills de rapporterats.",
					sql: `select ${PERIOD} as period, round(sum(gross_profit), 0) as bruttovinst, round(sum(revenue), 0) as omsättning ${FACTS} group by period order by period`,
					chart: {
						type: "combo",
						xKey: "period",
						yKeys: ["bruttovinst", "omsättning"],
						lineKeys: ["omsättning"],
						unit: "kr",
					},
					usesDateRange: true,
					usesBucket: true,
					usesFilters: true,
				},
			},
		],
		// Resurser: four cards, then the combo panel.
		[
			{
				prompt: "Nekade pass totalt",
				spec: card(
					"Nekade pass",
					"Pass på uppdrag som nekats.",
					`select count(*) filter (where is_denied) as nekade_pass ${FACTS}`,
					"nekade_pass"
				),
			},
			{
				prompt: "Ansökningar totalt",
				spec: card(
					"Ansökningar",
					"Nya konsultkonton via rekrytering, skapade i perioden.",
					"select count(*) as ansökningar from consultants where is_recruit and created >= :from and created < date :to + 1",
					"ansökningar"
				),
			},
			{
				prompt: "Arbetande konsulter totalt",
				spec: card(
					"Arbetande konsulter",
					"Konsulter med rapporterad arbetstid i perioden.",
					`select count(distinct consultant_id) filter (where is_worked) as arbetande_konsulter ${FACTS}`,
					"arbetande_konsulter"
				),
			},
			{
				prompt: "Aktiva konsulter",
				spec: card(
					"Aktiva konsulter",
					"Konsulter med aktiv status sista dagen i perioden.",
					"select count(distinct consultant_id) as aktiva_konsulter from consultant_status where active and start_date <= :to and (end_date is null or end_date >= :to)",
					"aktiva_konsulter"
				),
			},
		],
		[
			{
				prompt: "Resurser: bokade pass som staplar och sålda timmar som linje per period",
				spec: {
					title: "Resurser",
					description:
						"Bokade pass (staplar, pass med tidrapport) och sålda timmar (linje) per period.",
					sql: `select ${PERIOD} as period, count(*) filter (where tr_status is not null) as bokade_pass, round(sum(billable_hours), 1) as sålda_timmar ${FACTS} group by period order by period`,
					chart: {
						type: "combo",
						xKey: "period",
						yKeys: ["bokade_pass", "sålda_timmar"],
						lineKeys: ["sålda_timmar"],
						unit: "pass",
						lineUnit: "h",
					},
					usesDateRange: true,
					usesBucket: true,
					usesFilters: true,
				},
			},
		],
	],
};

/** Templates offered under "Ny från mall". Add new ones here. */
export const REPORT_TEMPLATES: ReportTemplate[] = [KPI_TEMPLATE];
